// ============================================================================
// TrimaRankingQuestion — One question screen of the ranking phase
// ============================================================================
// Renders:
//   - Question label
//   - Brief instruction (only on Q1 & Q2 for friction reduction)
//   - "↑ Le plus représentatif" marker
//   - List of 5 draggable cards (TrimaRankingCard)
//   - "↓ Le moins représentatif" marker
//   - Validate button
//
// Drag & drop powered by @dnd-kit. The drag axis is restricted to vertical to
// avoid accidental horizontal swipes interfering.
//
// Validation: button is always active — the user can submit any ordering,
// including the BE-provided initial one (which counts as a valid response).
// ============================================================================

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { getLocaleValue } from 'helpers/utils';
import Button from 'components/common/button/Button';
import {
  TrimaRankingAnswer,
  TrimaRankingQuestion as TrimaRankingQuestionType,
  TrimaRankingResponse,
} from 'pages/questionnaire-trima/types/trima.types';
import TrimaRankingCard from 'pages/questionnaire-trima/slides/ranking/TrimaRankingCard';
import { TrimaRankingQuestionWrapper } from 'pages/questionnaire-trima/slides/ranking/TrimaRankingQuestion.styled';

export interface TrimaRankingQuestionProps {
  question: TrimaRankingQuestionType;
  questionIndex: number; // 0-based
  totalQuestions: number;
  /** Called with the final ranking when user clicks Validate. */
  onValidate: (response: TrimaRankingResponse) => void;
}

const TrimaRankingQuestion: React.FC<TrimaRankingQuestionProps> = ({
  question,
  questionIndex,
  onValidate,
}) => {
  const { t, i18n } = useTranslation('trima');

  // Local ordering state, initialized from the BE-provided answer order.
  // The order of this array IS the ranking: index 0 = "most representative",
  // last index = "least representative".
  const [answers, setAnswers] = useState<TrimaRankingAnswer[]>(question.answers);

  // Re-initialize when the question changes (parent moves to next question).
  React.useEffect(() => {
    setAnswers(question.answers);
  }, [question.id, question.answers]);

  // Sensors: pointer for desktop, touch for mobile. Distance/delay tuned to
  // distinguish a tap from a drag and avoid scrolling conflicts.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = answers.findIndex((a) => a.id === active.id);
    const newIndex = answers.findIndex((a) => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setAnswers((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const handleValidate = () => {
    onValidate({
      questionId: question.id,
      rankedAnswerIds: answers.map((a) => a.id),
    });
  };

  // Instruction is shown on the first two questions only, then disappears
  // to reduce visual noise once the user has internalized the mechanic.
  const showInstruction = questionIndex < 2;

  return (
    <TrimaRankingQuestionWrapper>
      <p className="trima-question-label">{getLocaleValue(question.label, i18n.language)}</p>

      {showInstruction && (
        <p className="trima-instruction">{t('ranking.instruction')}</p>
      )}

      <div className="trima-rank-marker top">
        <span>↑</span>
        <span>{t('ranking.rank-top')}</span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={answers.map((a) => a.id)} strategy={verticalListSortingStrategy}>
          <div className="trima-cards-list">
            {answers.map((answer, idx) => (
              <TrimaRankingCard
                key={answer.id}
                answer={answer}
                position={idx}
                total={answers.length}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="trima-rank-marker bottom">
        <span>↓</span>
        <span>{t('ranking.rank-bottom')}</span>
      </div>

      <div className="trima-validate-row">
        <Button fill="solid" expand color="accent" onClick={handleValidate}>
          <span>{t('ranking.validate')}</span>
        </Button>
      </div>
    </TrimaRankingQuestionWrapper>
  );
};

export default TrimaRankingQuestion;
